/**
 * Headless presentation helpers for catalogue items — the small derivations
 * the grid tile and the configurator both need, kept platform-neutral so the
 * web shell and any future shell render the same labels.
 *
 * Icon resolution stays in the shell (it binds a concrete icon component);
 * everything here is pure data.
 */

import type { CatalogueItem, FulfilmentType } from "../data/catalogue-types";
import { isCountField } from "./catalogue-custom-value";

/** Plain-English label for a bare fulfilment type — the dictionary item
 *  page's header line, which has no `CatalogueItem` to hang a full
 *  `fulfilmentBadge` off (no stock level, no lead time to fold in). */
export function fulfilmentLabel(fulfilment: FulfilmentType): string {
  switch (fulfilment) {
    case "stocked":
      return "In stock";
    case "bought_in":
      return "Bought-in";
    default:
      return "Made to order";
  }
}

/** True when a field must be set up before the item has a price (a die, a
 *  bare panel size) — the item can't show a firm "from" price. A count
 *  (`Versions`) starts at its minimum, and a value that changes no cost (the
 *  NCR start number) has no bearing on the price, so neither counts. */
export function needsSetup(item: CatalogueItem): boolean {
  return item.fields.some(
    (f) =>
      f.mode === "always_custom" && !isCountField(f) && f.affectsCost !== false,
  );
}

export type FulfilmentTone = "made" | "stock" | "bought";

export interface FulfilmentBadge {
  label: string;
  tone: FulfilmentTone;
}

/** The fulfilment badge for a tile: made-to-order, in-stock (with level), or bought-in (with lead). */
export function fulfilmentBadge(item: CatalogueItem): FulfilmentBadge {
  switch (item.fulfilment) {
    case "stocked":
      return {
        label:
          item.stockLevel != null
            ? `In stock · ${item.stockLevel}`
            : "In stock",
        tone: "stock",
      };
    case "bought_in":
      return {
        label: item.leadTime ? `Bought-in · ${item.leadTime}` : "Bought-in",
        tone: "bought",
      };
    default:
      return { label: "Made to order", tone: "made" };
  }
}

export type TilePrice =
  { kind: "from"; amount: number; quantity: number } | { kind: "on_setup" };

/**
 * The tile price: a firm "from" price at the default quantity, unless the
 * item has an always-custom field (size/die/variable data) — then it's
 * priced on setup, or unless no live price could be derived at all (nothing
 * cached yet, or no price group resolves).
 *
 * `alwaysCustom` lets browse-listing tiles pass the server-computed flag —
 * the listing ships quantity breaks (the rungs) but not fields, so
 * inspecting `item.fields` would wrongly read "not custom" there.
 *
 * `indicativePricePence` is `CatalogueItemTile.indicativePricePence`,
 * resolved server-side from the item's cheapest cached manufacturing cost
 * (`list_catalogue_item_tiles` -> `PricingPort.resolve_indicative_price`).
 * This function never re-derives a price from `item.quantityBreaks` itself —
 * the manual quantity-break list price is retired (99_outstanding_setup.md);
 * a caller with no indicative price (e.g. the settings catalogue editor grid,
 * which does not fetch cost-derived pricing) sees "Priced on setup" rather
 * than a stale or invented number.
 */
export function tilePrice(
  item: CatalogueItem,
  alwaysCustom: boolean = needsSetup(item),
  indicativePricePence: number | null = null,
): TilePrice {
  if (alwaysCustom || indicativePricePence == null) {
    return { kind: "on_setup" };
  }
  return {
    kind: "from",
    amount: indicativePricePence / 100,
    quantity: item.defaultQuantity,
  };
}

/** Sorted, de-duplicated standard quantities for the Quantity chip row. */
export function standardQuantities(item: CatalogueItem): number[] {
  return [...new Set(item.quantityBreaks.map((b) => b.qty))].sort(
    (a, b) => a - b,
  );
}
